import type { Locale } from './locale';

export const figureCatalog = {
  userDevice: { en: 'User’s device', ja: 'ユーザーのデバイス' },
  yourInfrastructure: { en: 'Your infrastructure', ja: '自社のインフラ' },
  policyChecks: { en: 'Policy checks', ja: 'ポリシーの検証' },
  signedTransaction: { en: 'Signed transaction', ja: '署名済み取引' },
  thresholdExplanation: {
    en: '2-of-2 threshold · neither share signs alone',
    ja: '2-of-2しきい値署名 · 片方のシェアだけでは署名不可',
  },
  rest: { en: 'rest', ja: '待機中' },
  twoShares: { en: 'two shares', ja: '2つのシェア' },
  checkingPolicy: { en: 'policy checks', ja: 'ポリシーを検証中' },
  authorized: { en: 'authorized', ja: '認可済み' },
  signing: { en: 'signing', ja: '署名中' },
  signed: { en: 'signed', ja: '署名済み' },
  signedShort: { en: 'Signed', ja: '署名済み' },
  aligning: { en: 'aligning', ja: '位置を調整中' },
  pairingCode: { en: 'pairing code', ja: 'ペアリングコード' },
  scanning: { en: 'scanning', ja: 'スキャン中' },
  confirming: { en: 'confirming', ja: '確認中' },
  linked: { en: 'linked', ja: 'リンク済み' },
  pickingMiddle: { en: 'picking up middle', ja: '中央の輪を回転中' },
  pickingRear: { en: 'picking up rear', ja: '奥の輪を回転中' },
  aligningRear: { en: 'aligning rear', ja: '奥の輪を調整中' },
  aligningMiddle: { en: 'aligning middle', ja: '中央の輪を調整中' },
  aligningFront: { en: 'aligning front', ja: '手前の輪を調整中' },
  pinDropping: { en: 'pin dropping', ja: 'ピンを解除中' },
  unlocked: { en: 'unlocked', ja: 'ロック解除済み' },
  resetting: { en: 'resetting', ja: '元の位置に戻っています' },
  keyReadout: { en: 'key', ja: '鍵' },
  rack: { en: 'rack', ja: 'ラック' },
  bay: { en: 'bay', ja: 'スロット' },
  signingInstruction: {
    en: 'Animate two independent key shares authorizing one signed transaction',
    ja: '独立した2つの鍵シェアによる取引の認可を表示',
  },
  signingMeaning: {
    en: 'Two key shares rise from their bases. Three policies approve one signed transaction. Hover or press Enter to animate.',
    ja: '2つの鍵シェアと3つのポリシーで1つの取引を承認します。ポインターを重ねるかEnterキーで再生します。',
  },
  linkingInstruction: {
    en: 'Pair two devices by scanning a code. Hover or press Enter to animate.',
    ja: 'コードをスキャンして2つのデバイスをペアリングします。ポインターを重ねるかEnterキーで再生します。',
  },
  linkingMeaning: {
    en: 'Align both phones to scan the pairing code, then confirm on both screens. Hover or press Enter.',
    ja: '2台のスマートフォンでペアリングコードを読み取り、両方の画面で確認します。ポインターを重ねるかEnterキーで再生します。',
  },
  keychainMeaning: {
    en: 'Four house keys threaded through small head holes. Hover a key to fan it out around the ring.',
    ja: 'リングにつながった4本の鍵。鍵にポインターを重ねると、リングを中心に開きます。',
  },
  interlockInstruction: {
    en: 'Turn the dial to engage and align three wheels, then release the locking pin',
    ja: 'ダイヤルで3つの輪をそろえて、ロックピンを解除します',
  },
  interlockMeaning: {
    en: 'The dial picks up each wheel in turn. Aligned gates let the locking pin drop. Hover or press Enter.',
    ja: 'ダイヤルが順番に輪を回し、溝がそろうとロックピンが下がります。ポインターを重ねるかEnterキーで再生します。',
  },
  infrastructureMeaning: {
    en: 'Hover a cabinet to open its glass door and extend nearby servers. Leaving retracts the servers, then closes the door.',
    ja: 'ラックにポインターを重ねると扉が開き、サーバーが手前に出ます。ポインターを離すと元に戻ります。',
  },
} satisfies Record<string, Record<Locale, string>>;

// Each message requires both languages. Components and document builds use this catalog.
export const catalog = {
  pageTitle: {
    en: 'Embedded Wallet SDK with Passkeys | Seams',
    ja: 'パスキー対応の組み込みウォレットSDK | Seams',
  },
  pageDescription: {
    en: 'Embed passkey wallets with the Seams SDK. Add authentication, device linking, recovery, and transaction signing. Explore pricing and integration guides.',
    ja: 'パスキーで開く、ノンカストディアルの組み込みウォレット。認証、署名、復元をひとつのSDKで。',
  },
  heroTitle: {
    en: 'Embedded wallets without the lock-in.',
    ja: 'ロックインのない組み込みウォレット。',
  },
  heroDescription: {
    en: 'Add passkey wallets to your app, with device linking, recovery, and transaction signing. Use managed hosting or discuss an assisted deployment.',
    ja: 'アプリにパスキーウォレットを組み込み、デバイスのリンク、復元、トランザクション署名に対応。マネージド環境を利用するか、導入支援付きの環境構築をご相談ください。',
  },
  startFree: { en: 'Start free', ja: '無料で始める' },
  startBuilding: { en: 'Start building', ja: '開発を始める' },
  products: { en: 'Products', ja: 'プロダクト' },
  features: { en: 'Features', ja: '機能' },
  wallet: { en: 'Wallet', ja: 'ウォレット' },
  api: { en: 'API', ja: 'API' },
  supportedNetworks: { en: 'Supported networks', ja: '対応ネットワーク' },
  custodyTitle: { en: 'Custody and control, built in', ja: '資産の保管と管理を、ひとつに' },
  keysControl: { en: 'Keys you control', ja: '自分で管理する鍵' },
  nobodySignsAlone: { en: 'Nobody signs alone', ja: '単独では誰も署名できない' },
  splitKeysDescription: {
    en: 'Keys split between user device and server.',
    ja: 'ユーザーのデバイスとサーバーで鍵を分割。',
  },
  scopedPermissions: { en: 'Scoped permissions', ja: '範囲を限定した権限' },
  permissions: { en: 'Permissions', ja: '権限の管理' },
  permissionsDescription: {
    en: 'Limit what apps and agents can sign.',
    ja: 'アプリやエージェントの署名を制限。',
  },
  linkedDevices: { en: 'Linked devices', ja: 'リンク済みデバイス' },
  walletsNotLost: {
    en: 'Wallets your users do not lose',
    ja: 'ユーザーが失わないウォレット',
  },
  devicesDescription: {
    en: 'Link by QR. Recover with email or recovery codes.',
    ja: 'QRで連携。メールコードか復元コードで復元。',
  },
  noLockIn: { en: 'No lock-in', ja: 'ロックインなし' },
  ownAccounts: {
    en: 'Own your accounts. Leave any time.',
    ja: 'アカウントは自社のもの。いつでも移行可能。',
  },
  hostingDescription: {
    en: 'Apache-2.0 open source with key export.',
    ja: 'Apache-2.0で公開。鍵のエクスポートも可能。',
  },
  signingFigure: { en: 'Two shares, one signed transaction', ja: '2つのシェアで、1つの取引に署名' },
  custodyViews: { en: 'Custody views', ja: '資産管理の表示' },
  previousCustody: { en: 'Previous custody view', ja: '前の表示' },
  nextCustody: { en: 'Next custody view', ja: '次の表示' },
  resumeCycling: { en: 'Resume automatic cycling', ja: '自動切り替えを再開' },
  pauseCycling: { en: 'Pause automatic cycling', ja: '自動切り替えを停止' },
  observability: { en: 'Observability', ja: '稼働状況' },
  failureAlerts: {
    en: 'Failing services flagged as they happen',
    ja: 'サービスの障害をすぐに検知',
  },
  auditLogs: { en: 'Audit logs', ja: '監査ログ' },
  csvExport: { en: 'Export to CSV in one click', ja: 'ワンクリックでCSVに出力' },
  passkeySignup: { en: 'Passkey sign-up', ja: 'パスキーで登録' },
  signupDescription: {
    en: 'A non-custodial wallet in one call',
    ja: '1回の呼び出しでノンカストディアルウォレットを作成',
  },
  recovery: { en: 'Recovery', ja: '復元' },
  recoveryDescription: { en: 'Email or a linked device', ja: 'メールやリンク済みデバイスで復元' },
  signingSessions: { en: 'Signing sessions', ja: '署名セッション' },
  sessionsDescription: {
    en: 'An expiry and a use limit per unlock',
    ja: 'ロック解除ごとに有効期限と利用回数を設定',
  },
  approvals: { en: 'Approvals', ja: '承認' },
  approvalsDescription: { en: 'The owner confirms in the wallet', ja: '所有者がウォレットで確認' },
  integrationTitle: {
    en: 'Serverless wallets on Cloudflare, in a few API calls',
    ja: 'Cloudflare上のサーバーレスウォレットを、わずかなAPI呼び出しで',
  },
  exploreDocumentation: { en: 'Explore documentation', ja: 'ドキュメントを見る' },
  accountsApi: { en: 'Accounts and Wallets API', ja: 'アカウント・ウォレットAPI' },
  accountsDescription: {
    en: 'One passkey creates the account and its wallet. Recovery is built in.',
    ja: '1つのパスキーでアカウントとウォレットを作成。復元機能も備えています。',
  },
  registrationCode: { en: 'Register a wallet with the SDK', ja: 'SDKでウォレットを登録するコード' },
  sessionsApi: { en: 'Sessions and Approvals API', ja: 'セッション・承認API' },
  sessionsApiDescription: {
    en: 'One unlock opens a signing session with limits. Transactions that matter wait for the owner.',
    ja: '1回のロック解除で、制限付きの署名セッションを開始。重要な取引は所有者の承認を待ちます。',
  },
  signingCode: {
    en: 'Open a signing session and send a transfer',
    ja: '署名セッションを開始して送金するコード',
  },
  walletConsole: { en: 'Wallet console', ja: 'ウォレットコンソール' },
  consoleDescription: {
    en: 'Manage wallets, policies, and your team in one place. No code needed.',
    ja: 'ウォレット、ポリシー、チームをひとつの画面で管理。コードは不要です。',
  },
  openConsole: { en: 'Open wallet console', ja: 'コンソールを開く' },
  showConsole: { en: 'Show a console screen', ja: 'コンソールの画面を選択' },
  consolePreview: {
    en: 'Wallet console overview and observability pages (English)',
    ja: 'ウォレットコンソールの概要と稼働状況の画面',
  },
  security: { en: 'Security', ja: 'セキュリティ' },
  integration: { en: 'Integration', ja: '導入' },
  hostingPricing: { en: 'Hosting & pricing', ja: 'ホスティング・料金' },
  faq: { en: 'FAQs', ja: 'よくある質問' },
  custodialQuestion: { en: 'Is Seams custodial?', ja: 'Seamsが資産を管理するのですか？' },
  custodialAnswer: {
    en: 'No. Each key is split between the user’s device and the server, and a signature needs both. Seams cannot move your users’ funds, and your server cannot either. Exporting a key always needs the user’s fresh approval.',
    ja: 'いいえ。鍵はユーザーのデバイスとサーバーに分割され、署名には両方が必要です。Seamsはユーザーの資産を移動できず、お客様のサーバーも移動できません。鍵のエクスポートには、必ずユーザーによる新たな承認が必要です。',
  },
  custodyModel: { en: 'The custody model', ja: '資産管理の仕組み' },
  lostDeviceQuestion: {
    en: 'What if a user loses their device?',
    ja: 'デバイスを紛失した場合は？',
  },
  lostDeviceAnswer: {
    en: 'They recover on a new device with their email or recovery code, then add a new passkey. The wallet and its address stay the same, so no funds have to move.',
    ja: 'メールまたは復元コードを使って新しいデバイスでアクセスを復元し、新しいパスキーを追加します。ウォレットとアドレスは変わらないため、資産を移動する必要はありません。',
  },
  walletSessions: { en: 'Wallet Sessions', ja: 'ウォレットセッション' },
  apiKeysQuestion: { en: 'How do API keys work?', ja: 'APIキーはどのように機能しますか？' },
  apiKeysAnswer: {
    en: 'Your backend authenticates with API keys; end users and agents authenticate with passkeys and scoped credentials. Signing always requires the user-side key share, so API keys alone can never move funds or export keys.',
    ja: 'バックエンドはAPIキーで認証し、ユーザーやエージェントはパスキーや範囲を限定した認証情報で認証します。署名には常にユーザー側の鍵シェアが必要です。APIキーだけで資産の移動や鍵のエクスポートはできません。',
  },
  signInMethods: { en: 'Sign-in methods', ja: 'サインイン方法' },
  networksQuestion: {
    en: 'Which networks are supported?',
    ja: 'どのネットワークに対応していますか？',
  },
  networksAnswer: {
    en: 'NEAR, Ethereum, Tempo and Arc, on mainnet and testnet.',
    ja: 'NEAR、Ethereum、Tempo、Arcのメインネットとテストネットに対応しています。',
  },
  hostingQuestion: { en: 'Can we run Seams ourselves?', ja: '自社でSeamsを運用できますか？' },
  hostingAnswer: {
    en: 'Yes, with our help. Seams runs on Cloudflare Workers, and our runtime build runs on Google Cloud and AWS virtual machines. Self-serve tooling is still coming. Contact us for early access.',
    ja: 'はい、当社のサポートのもとで運用できます。SeamsはCloudflare Workersで稼働し、当社のランタイムビルドはGoogle CloudとAWSの仮想マシンで動作します。セルフサービスのツールは準備中です。早期アクセスについてはお問い合わせください。',
  },
  architecture: { en: 'Architecture', ja: 'アーキテクチャ' },
  meteringQuestion: { en: 'How is usage metered?', ja: '利用料金はどのように計算されますか？' },
  meteringAnswer: {
    en: 'Free for up to 300 monthly active users. Growth costs $99 a month with 5,000 users, then $0.004 for each extra user.',
    ja: '月間アクティブユーザー300人までは無料です。Growthプランは月額$99で5,000ユーザーを含み、超過分は1人あたり$0.004です。',
  },
  seePricing: { en: 'See pricing', ja: '料金を見る' },
  embeddedWallet: { en: 'Embedded Wallet', ja: '組み込みウォレット' },
  custodyLink: { en: 'Custody Model', ja: '資産管理モデル' },
  platform: { en: 'Platform', ja: 'プラットフォーム' },
  authentication: { en: 'Authentication', ja: '認証' },
  authLink: { en: 'Authentication', ja: '認証' },
  passkeys: { en: 'Passkeys', ja: 'パスキー' },
  walletsSignatures: { en: 'Wallets & Signatures', ja: 'ウォレットと署名' },
  permissionsPolicy: { en: 'Permissions & Policy', ja: '権限とポリシー' },
  developers: { en: 'Developers', ja: '開発者向け' },
  documentation: { en: 'Documentation', ja: 'ドキュメント' },
  walletIframe: { en: 'Wallet Iframe', ja: 'ウォレットiframe' },
  resources: { en: 'Resources', ja: 'リソース' },
  guides: { en: 'Guides', ja: 'ガイド' },
  useCases: { en: 'Use Cases', ja: '活用事例' },
  pricing: { en: 'Pricing', ja: '料金' },
  socials: { en: 'Socials', ja: 'ソーシャル' },
  company: { en: 'Company', ja: '企業情報' },
  about: { en: 'About', ja: '会社概要' },
  blog: { en: 'Blog', ja: 'ブログ' },
  closingTitle: {
    en: 'Ship wallets this week. Keep your users for good.',
    ja: '今週リリース。\n顧客との関係はそのまま。',
  },
  closingDescription: {
    en: 'Passkey wallets with device linking and recovery. Free for up to 300 monthly active users.',
    ja: 'パスキー・復元・デバイス連携。\n月間アクティブユーザー300人まで無料。',
  },
  exploreDocs: { en: 'Explore docs', ja: 'ドキュメント' },
  siteFooter: { en: 'Site footer', ja: 'サイトフッター' },
  footerNavigation: { en: 'Footer navigation', ja: 'フッターナビゲーション' },
  seamsHome: { en: 'Seams home', ja: 'Seamsホーム' },
  walletHome: { en: 'Seams Wallet home', ja: 'Seams Walletホーム' },
  language: { en: 'Language', ja: '言語' },
  demoUnavailable: {
    en: 'The live demo is temporarily unavailable.',
    ja: 'ライブデモを一時的にご利用いただけません。',
  },
  demoExplore: {
    en: 'You can still explore the wallet features below.',
    ja: '下のセクションでウォレットの機能をご覧いただけます。',
  },
  reload: { en: 'Reload page', ja: 'ページを再読み込み' },
  demoLoading: { en: 'Loading live demo', ja: 'ライブデモを読み込み中' },
  demoEnglish: { en: 'Live wallet demo', ja: 'ウォレットのライブデモ' },
  previewTheme: { en: 'Preview theme', ja: 'テーマをプレビュー' },
  themePaper: { en: 'Paper', ja: 'ペーパー' },
  themeCarbon: { en: 'Carbon', ja: 'カーボン' },
  themePastel: { en: 'Pastel', ja: 'パステル' },
  themePastelDark: { en: 'Pastel Dark', ja: 'パステルダーク' },
  settings: { en: 'Settings', ja: '設定' },
  demoScreens: { en: 'Demo screens', ja: 'デモ画面' },
  previousDemo: { en: 'Previous demo screen', ja: '前のデモ画面' },
  nextDemo: { en: 'Next demo screen', ja: '次のデモ画面' },
  login: { en: 'Login', ja: 'ログイン' },
  transactions: { en: 'Transactions', ja: '取引' },
  accountRecovery: { en: 'Account recovery', ja: 'アカウントの復元' },
  componentsTitle: {
    en: 'Use built-in wallet components,\nor use your own.',
    ja: '標準のウォレットUIも、独自のUIも。',
  },
  secondsUnit: { en: 's', ja: '秒' },
  customComponents: { en: 'Custom Components', ja: '独自のUI' },
  recoverLink: { en: 'Recover and Link Devices', ja: '復元・デバイスのリンク' },
  components: { en: 'Components', ja: 'コンポーネント' },
  pausePlayback: { en: 'Pause playback', ja: '再生を一時停止' },
  playPlayback: { en: 'Play playback', ja: '再生' },
  transactionModal: { en: 'Transaction modal', ja: '取引の確認画面' },
  predictionMarket: { en: 'Prediction market', ja: '予測市場' },
  voiceId: { en: 'VoiceID', ja: 'VoiceID' },
  poweredBy: { en: 'Powered by seams', ja: 'seamsで構築' },
  welcomeBack: { en: 'Welcome back', ja: 'おかえりなさい' },
  walletReady: { en: 'Your wallet is ready.', ja: 'ウォレットの準備ができました。' },
  unlockPasskey: { en: 'Unlock with passkey', ja: 'パスキーでロック解除' },
  recoverWallet: { en: 'Recover your wallet', ja: 'ウォレットを復元' },
  sameWallet: {
    en: 'A fresh start, with the same wallet.',
    ja: '同じウォレットで、新たなスタート。',
  },
  continue: { en: 'Continue', ja: '続ける' },
  order: { en: 'Studio North · Order #1042', ja: 'Studio North · 注文 #1042' },
  payWith: { en: 'Pay with', ja: '支払い方法' },
  networkFee: { en: 'Network fee', ja: 'ネットワーク手数料' },
  included: { en: 'Included', ja: '料金に含む' },
  payAmount: { en: 'Pay $24.00', ja: '$24.00を支払う' },
  transferConfirmed: { en: 'Transfer confirmed', ja: '送金を確認しました' },
  transferArrived: { en: 'Your transfer reached its destination.', ja: '送金先に届きました。' },
  to: { en: 'To', ja: '送金先' },
  network: { en: 'Network', ja: 'ネットワーク' },
  status: { en: 'Status', ja: 'ステータス' },
  confirmed: { en: 'Confirmed', ja: '確認済み' },
  biometricAuth: { en: 'Biometric Authentication', ja: '生体認証' },
  simpleWallets: { en: 'Safe, simple wallets', ja: '安全でシンプルなウォレット' },
  simpleWalletsDescription: {
    en: 'Passkey sign-in, email recovery, and device linking',
    ja: 'パスキーでサインイン、メールで復元、デバイスをリンク',
  },
  globalSelfHosted: { en: 'Global or self-hosted', ja: 'グローバル展開・セルフホスト' },
  globalSelfHostedDescription: {
    en: 'Run near your users, or on your own cloud',
    ja: 'ユーザーの近くでも、自社のクラウドでも運用',
  },
  biometricDescription: {
    en: 'Passkeys with Face ID and Touch ID, plus VoiceID.',
    ja: 'Face IDやTouch IDで使えるパスキーに加え、VoiceIDにも対応。',
  },
  allFeatures: { en: 'All features', ja: 'すべての機能' },
  componentsCard: { en: 'Wallet components', ja: 'ウォレットのコンポーネント' },
  componentsCardDescription: {
    en: 'Built-in sign-in, payment, and recovery screens, or your own',
    ja: 'サインイン、支払い、復元の標準画面も、独自のUIも',
  },
  liveDemo: { en: 'Live demo', ja: 'ライブデモ' },
  serverlessInfra: { en: 'Serverless infra', ja: 'サーバーレス基盤' },
  getStarted: { en: 'Get Started', ja: 'はじめに' },
  getStartedDescription: {
    en: 'Install the SDK, create a wallet, and sign with policy',
    ja: 'SDKを導入し、ウォレットを作成してポリシーに従って署名',
  },
  toolsGuides: { en: 'Tools and Guides', ja: 'ツールとガイド' },
  toolsDescription: {
    en: 'Auth, sessions, wallet UI, policy, and the SDK reference',
    ja: '認証、セッション、ウォレットUI、ポリシー、SDKリファレンス',
  },
  useCasesNav: { en: 'Use cases', ja: '活用事例' },
  useCasesDescription: {
    en: 'Mandates, delegated agents, and agent-driven commerce',
    ja: '権限の委任、エージェント、エージェントによる取引',
  },
  architectureDescription: {
    en: 'How passkeys, policy, and signing fit together',
    ja: 'パスキー、ポリシー、署名が連携する仕組み',
  },
  readDocs: { en: 'Read the docs', ja: 'ドキュメントを読む' },
  releases: { en: 'Releases', ja: 'リリース情報' },
  plans: { en: 'Plans', ja: 'プラン' },
  free: { en: 'Free', ja: '無料' },
  freeDescription: { en: 'Up to 300 monthly active users', ja: '月間アクティブユーザー300人まで' },
  growthDescription: {
    en: '5,000 users included, then $0.004 per user',
    ja: '5,000ユーザーまで含む。超過分は1人あたり$0.004',
  },
  monthlyPrice: { en: '$99 / mo', ja: '$99 / 月' },
  // Placeholder Pro prices, as on the site's pricing page.
  proDescription: {
    en: '100,000 users included, then $0.0035 per user',
    ja: '100,000ユーザーまで含む。超過分は1人あたり$0.0035',
  },
  proMonthlyPrice: { en: '$399 / mo', ja: '$399 / 月' },
  enterpriseDescription: {
    en: 'From $0.003 per user on committed volume',
    ja: '利用数の契約に応じて1人あたり$0.003から',
  },
  custom: { en: 'Custom', ja: '個別見積もり' },
  estimatePrice: { en: 'Estimate your price', ja: '料金を試算' },
  estimateDescription: {
    en: 'Set your users and see the monthly total',
    ja: 'ユーザー数を設定して月額料金を確認',
  },
  comparePlans: { en: 'Compare plans', ja: 'プランを比較' },
  primaryNavigation: { en: 'Primary', ja: 'メインナビゲーション' },
  dashboard: { en: 'Dashboard', ja: 'ダッシュボード' },
  openMenu: { en: 'Open menu', ja: 'メニューを開く' },
  closeMenu: { en: 'Close menu', ja: 'メニューを閉じる' },
  back: { en: 'Back', ja: '戻る' },
  createWallet: { en: 'Create a wallet', ja: 'ウォレットを作成' },
  yourKeys: { en: 'Your keys. Your next chapter.', ja: 'あなたの鍵で、新たな一歩を。' },
  createPasskey: { en: 'Create with passkey', ja: 'パスキーで作成' },
  walletName: { en: 'Wallet name', ja: 'ウォレット名' },
  unlockWallet: { en: 'Unlock wallet', ja: 'ウォレットをロック解除' },
  welcomeBackPeriod: { en: 'Welcome back.', ja: 'おかえりなさい。' },
  continuePasskey: { en: 'Continue with passkey', ja: 'パスキーで続ける' },
  startOver: { en: 'Start over', ja: '最初からやり直す' },
  linkDevice: { en: 'Link this device', ja: 'このデバイスをリンク' },
  scanDevice: {
    en: 'Scan this code with your other device to connect this one.',
    ja: '別のデバイスでこのコードをスキャンしてリンクします。',
  },
  createPasskeyButton: { en: 'Create passkey', ja: 'パスキーを作成' },
  recoverWalletHeading: { en: 'Recover wallet', ja: 'ウォレットを復元' },
  useRecoveryCode: {
    en: 'Use your recovery code to restore access.',
    ja: '復元コードを使ってアクセスを復元します。',
  },
  recoverPasskey: { en: 'Recover with Passkey', ja: 'パスキーで復元' },
  walletRecovered: { en: 'Wallet recovered', ja: 'ウォレットを復元しました' },
  newPasskey: { en: 'Create a new passkey', ja: '新しいパスキーを作成' },
  unlockContinue: {
    en: 'Unlock your wallet to continue.',
    ja: 'ウォレットをロック解除して続けます。',
  },
  secureDevice: {
    en: 'Secure your wallet on this device.',
    ja: 'このデバイスでウォレットを保護します。',
  },
  newPasskeyButton: { en: 'Create new passkey', ja: '新しいパスキーを作成' },
  recoverGoogle: { en: 'Recover with Google', ja: 'Googleで復元' },
  restoreAccess: {
    en: 'Restore access to your wallet.',
    ja: 'ウォレットへのアクセスを復元します。',
  },
  continueGoogle: { en: 'Continue with Google', ja: 'Googleで続ける' },
  transferEth: { en: 'Transfer ETH', ja: 'ETHを送金' },
  transferNear: { en: 'Transfer NEAR', ja: 'NEARを送金' },
  reviewTransfer: { en: 'Review your transfer', ja: '送金内容を確認' },
  confirmPasskey: { en: 'Confirm with passkey', ja: 'パスキーで確認' },
  confirmTrade: { en: 'Confirm trade', ja: '取引を確定' },
  tryAgain: { en: 'Try again', ja: 'もう一度試す' },
  reviewTrade: { en: 'Review your trade', ja: '取引内容を確認' },
  voiceTradeAction: { en: 'Swap {asset} for {receiveAsset}', ja: '{asset}を{receiveAsset}に交換' },
  minimumReceived: { en: 'Minimum received', ja: '最低受取額' },
  slippageLimit: { en: 'Slippage limit', ja: 'スリッページ上限' },
  receivingAccount: { en: 'Receiving account', ja: '受取アカウント' },
  quoteValidity: { en: 'Quote validity', ja: '見積もりの有効期間' },
  exampleQuoteValidity: { en: '30 seconds after approval starts', ja: '承認開始から30秒' },
  unlockWithVoiceId: { en: 'Unlock with VoiceID', ja: 'VoiceIDでロック解除' },
  voiceListening: { en: 'Listening', ja: '音声を確認中' },
  voiceChecking: { en: 'Checking your voice…', ja: '声を照合中…' },
  cancel: { en: 'Cancel', ja: 'キャンセル' },
  outcome: { en: 'Outcome', ja: '予測結果' },
  yes: { en: 'Yes', ja: 'はい' },
  no: { en: 'No', ja: 'いいえ' },
  buy: { en: 'Buy', ja: '購入' },
  reviewPurchase: { en: 'Review purchase', ja: '購入内容を確認' },
  youPay: { en: 'You pay', ja: '支払い額' },
  units: { en: 'units', ja: '単位' },
  payoutIf: { en: 'Payout if', ja: '的中時の受取額：' },
  price: { en: 'Price', ja: '価格' },
  minimumPayout: { en: 'Minimum payout', ja: '最低受取額' },
  tradingFee: { en: 'Trading fee', ja: '取引手数料' },
  includedParentheses: { en: '(included)', ja: '（料金に含む）' },
  quoteTime: { en: 'Quote time remaining', ja: '見積もりの残り時間' },
  confirmWallet: { en: 'Confirm in wallet', ja: 'ウォレットで確認' },
  approvePurchase: { en: 'Approve purchase', ja: '購入を承認' },
  purchaseConfirmed: { en: 'Purchase confirmed.', ja: '購入を確認しました。' },
  checkoutClosed: { en: 'Checkout closed.', ja: '購入画面を閉じました。' },
  checkoutFailed: { en: 'Checkout could not open.', ja: '購入画面を開けませんでした。' },
  declinedWallet: { en: 'Declined in the wallet.', ja: 'ウォレットで拒否しました。' },
  transactionReceipt: { en: 'Transaction receipt', ja: '取引の結果' },
  walletApproval: { en: 'Wallet approval', ja: 'ウォレットで承認' },
  marketQuestion: {
    en: "Will the AP Royal Oak close above its Sotheby's estimate?",
    ja: 'AP Royal Oakはサザビーズの予想価格を上回る？',
  },
  marketYes: { en: 'AP Royal Oak closes above its estimate', ja: 'AP Royal Oakが予想価格を上回る' },
  marketNo: {
    en: 'AP Royal Oak closes at or below its estimate',
    ja: 'AP Royal Oakが予想価格以下になる',
  },
  next: { en: 'Next', ja: '次へ' },
  waitingApproval: { en: 'Waiting for approval', ja: '承認を待っています' },
  deviceApproved: { en: 'Device approved', ja: 'デバイスを承認しました' },
  readyOnDevice: {
    en: 'Your wallet is ready on this device.',
    ja: 'このデバイスでウォレットを利用できます。',
  },
  walletCreated: { en: 'Wallet created', ja: 'ウォレットを作成しました' },
  signedIn: { en: "You're in", ja: 'サインインしました' },
  walletUnlocked: { en: 'Your wallet is unlocked.', ja: 'ウォレットのロックを解除しました。' },
  creatingWallet: { en: 'Creating passkey wallet…', ja: 'パスキーウォレットを作成中…' },
  unlockingWallet: { en: 'Unlocking wallet…', ja: 'ウォレットをロック解除中…' },
  signingIn: { en: 'Signing you in…', ja: 'サインイン中…' },
  checkRecovery: {
    en: 'Check your recovery code and try again.',
    ja: '復元コードを確認して、もう一度お試しください。',
  },
  deviceLinked: { en: 'Device linked', ja: 'デバイスをリンクしました' },
  quoteExpired: { en: 'Quote expired', ja: '見積もりの期限が切れました' },
  quoteExpires: { en: 'Quote expires in', ja: '見積もりの有効期間' },
  refreshQuote: { en: 'Refresh quote', ja: '見積もりを更新' },
  backToMarket: { en: 'Back to market', ja: '市場に戻る' },
  unitsPerPosition: { en: 'units per position', ja: '単位 / ポジション' },
  purchaseNote: {
    en: 'Each position pays 1 unit if {side} wins. If the price moves, you get at least the minimum payout.',
    ja: '「{side}」が的中すると、1ポジションにつき1単位を受け取ります。価格が変動しても、最低受取額以上を受け取れます。',
  },
  ...figureCatalog,
} satisfies Record<string, Record<Locale, string>>;

export type MessageKey = keyof typeof catalog;

export function translate(locale: Locale, key: MessageKey): string {
  return catalog[key][locale];
}
